import React, { useState } from 'react';
import CollectionPreferences, { CollectionPreferencesProps } from '@amzn/awsui-components-console/collection-preferences';
import Header from '@amzn/awsui-components-console/header';
import Pagination from '@amzn/awsui-components-console/pagination';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import Table from '@amzn/awsui-components-console/table';
import Modal from '@amzn/awsui-components-console/modal';
import Link from '@amzn/awsui-components-console/link';
import Container from '@amzn/awsui-components-console/container';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Box from '@amzn/awsui-components-console/box';
import Flashbar from '@amzn/awsui-components-console/flashbar';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
	COLUMN_DEFINITIONS,
	DEFAULT_PREFERENCES,
	COLLECTION_PREFERENCES,
	isLegacyWorkflow,
} from './ExecuteWorkflowConstants';
import WorkflowDetails from '../ManageWorkflows/WorkflowDetailsView';
import { TableWithNoData } from 'src/components/commonComponents/table/tableWithNoData';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';
import { useAuthContext } from 'src/providers/AuthContext';
import { useWorkflowData } from '../commonComponents/WorkflowTable/useWorkflowData';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import { useFavoritesStore } from 'src/stores/favoriteWorkflowsStore/favoritesStore';
import { useGlobalExecutionGate } from 'src/hooks/useGlobalExecutionGate';
import { useExecuteWorkflowPermissions } from './useExecuteWorkflowPermissions';

const getEmptyTableMessage = (
	hasNoExecutableWorkflows: boolean,
	permissionError: string | null,
	listWorflowDefinitionError: string | null
): string => {
	if (hasNoExecutableWorkflows) {
		return "You don't seem to have permissions to execute any workflow. Go to Manage Workflows to review the permissions.";
	}

	return permissionError || listWorflowDefinitionError || 'No workflows available for execution.';
};

export default function ExecuteWorkflowTable() {
	const { isEffectivelyBlocked } = useGlobalExecutionGate();

	const [preferences, setPreferences] =
		useState<Partial<CollectionPreferencesProps.Preferences>>(
			() => ({ ...DEFAULT_PREFERENCES, stripedRows: getDefaultStripedRows() })
		);
	const [selectedWorkflows, setSelectedWorkflows] = useState<
		WorkflowDefinition[]
	>([]);
	const [selectedWorkflowName, setSelectedWorkflowName] =
		useState<WorkflowDefinition>();

	const [visible, setVisible] = useState(false);
	const authContext = useAuthContext();
	const currentUserName = authContext?.userName;

	const { isLoading: listWorflowDefinitionLoading, error: listWorflowDefinitionError, allWorkflows } = useWorkflowData();

	const { executableWorkflows, executeWorkflowIds, isCheckingPermissions, permissionError } = useExecuteWorkflowPermissions(
		allWorkflows,
		currentUserName
	);

	const { favoriteWorkflowIds } = useFavoritesStore();

	const listWorkflowData = executableWorkflows;

	const hasNoExecutableWorkflows = !isCheckingPermissions && !listWorflowDefinitionLoading && executableWorkflows.length === 0 && !permissionError && !listWorflowDefinitionError;

	const {
		items,
		filteredItemsCount,
		collectionProps,
		filterProps,
		paginationProps,
	} = useCollection(listWorkflowData, {
		filtering: {
			empty: (
				<TableWithNoData
					message={getEmptyTableMessage(
						hasNoExecutableWorkflows,
						permissionError,
						listWorflowDefinitionError
					)}
					actionButton={
						hasNoExecutableWorkflows && (
							<Button
								variant="primary"
								href="#/manageWorkflows"
								iconName="external"
							>
								Manage Workflows
							</Button>
						)
					}
				/>
			),
			noMatch: <TableWithNoData message={'No results found for your query'} />,
		},
		pagination: { pageSize: preferences.pageSize },
		sorting: {},
	});

	const COLUMN_DEFINITIONS_NAME = {
		id: 'workflowDefinitionName',
		header: 'Workflow Name',
		cell: (item) => (
			<Link
				data-testid={`show_workflow_${item.workflowDefinitionName}`}
				variant="primary"
				onFollow={(e) => {
					e.preventDefault();
					setSelectedWorkflowName(item);
					setVisible(true);
				}}
			>
				{item.workflowDefinitionName}
			</Link>
		),
		minWidth: '180px',
		sortingField: 'workflowDefinitionName',
	};

	function filterCounter(count) {
		return `${count} ${count === 1 ? 'match' : 'matches'}`;
	}

	const isWorkflowSelected = selectedWorkflows.length > 0;
	const isSelectedLegacy = isWorkflowSelected && isLegacyWorkflow(selectedWorkflows[0].workflowId);

	interface TableHeaderProps {
		selectedWorkflows: WorkflowDefinition[];
		counter: string;
	}

	const TableHeader = ({ selectedWorkflows, counter }: TableHeaderProps) => {
		// Block execute-permission workflows with allowedDuringPeak: false.
		// Submit-only workflows and allowedDuringPeak: true workflows bypass the global block.
		const selected = selectedWorkflows[0];
		const hasExecutePerm = selected && executeWorkflowIds.has(selected.workflowId);
		const isAllowedDuringPeak = selected?.allowedDuringPeak ?? false;
		const isBlockedByGlobalAlert = isEffectivelyBlocked && hasExecutePerm && !isAllowedDuringPeak;

		const isDisabled = isBlockedByGlobalAlert || selectedWorkflows.length === 0 ||
			selectedWorkflows[0].status !== 'ACTIVE' ||
			isLegacyWorkflow(selectedWorkflows[0].workflowId);

		return (
			<Header
				variant="awsui-h1-sticky"
				counter={counter}
				actions={
					<SpaceBetween direction="horizontal" size="s">
						<Button
							disabled={isDisabled}
							href={
								isWorkflowSelected
									? `#/executeWorkflow/${selectedWorkflows[0].workflowId}`
									: '#'
							}
							variant="primary"
						>
							Start Workflow
						</Button>
						<Button
							disabled={isDisabled}
							href={
								isWorkflowSelected
									? `#/bulkExecution/${selectedWorkflows[0].workflowId}`
									: '#'
							}
						>
							Bulk Execution
						</Button>
						<Button
							disabled={selectedWorkflows.length === 0 || selectedWorkflows[0].status !== 'ACTIVE'}
							href={
								isWorkflowSelected
									? `#/manageExecution?workflowType=${selectedWorkflows[0].workflowId}`
									: '#/manageExecution'
							}
						>
							Track Executions
						</Button>
					</SpaceBetween>
				}
			>
				Execute Workflows
			</Header>
		);
	};

	return (
		<Container>
			<SpaceBetween direction="vertical" size="l">
				{isSelectedLegacy && (
					<Flashbar
						items={[
							{
								type: 'warning',
								content: (
									<>
										This is a legacy workflow and cannot be executed from this page. Please refer to the{' '}
										<Link href="https://w.amazon.com/bin/view/SNOW/UserGuide" external>
											User Guide
										</Link>{' '}
										for instructions on how to execute legacy workflows.
									</>
								),
								id: 'legacy-workflow-warning',
							},
						]}
					/>
				)}
				{selectedWorkflowName && (
					<Modal
						size="large"
						data-testid="view-workflow-buttons-modal"
						onDismiss={() => setVisible(false)}
						visible={visible}
						header={`${selectedWorkflowName.workflowDefinitionName} Workflow Details`}
						footer={<WorkflowDetails workflowData={selectedWorkflowName} />}
					/>
				)}
				<Table
					{...collectionProps}
					resizableColumns
					stickyHeader
					loadingText={isCheckingPermissions ? 'Checking permissions...' : 'Loading resources'}
					loading={listWorflowDefinitionLoading || isCheckingPermissions}
					columnDefinitions={[COLUMN_DEFINITIONS_NAME, ...COLUMN_DEFINITIONS]}
					items={items}
					wrapLines
					stripedRows={preferences.stripedRows}
					header={
						<>
							<Box padding={{ top: 'xxs' }}>
								<TableHeader
									selectedWorkflows={selectedWorkflows}
									counter={`(${filteredItemsCount})`}
								/>
							</Box>
							<Box variant="p" color="text-body-secondary" padding={{ top: 'xxs' }}>
								<em>Displays only workflows for which {currentUserName} has execute or submit permissions</em>
							</Box>
							{favoriteWorkflowIds.length > 0 && (
								<ExpandableSection headerText="★ Favorites">
									<SpaceBetween direction="horizontal" size="xs">
										{executableWorkflows
											.filter((w) => favoriteWorkflowIds.includes(w.workflowId))
											.map((w) => (
												<Button
													key={w.workflowId}
													variant="inline-link"
													onClick={() => {
														setSelectedWorkflows([w]);
														filterProps.onChange({
															detail: { filteringText: w.workflowDefinitionName },
														});
													}}
												>
													{w.workflowDefinitionName}
												</Button>
											))}
									</SpaceBetween>
								</ExpandableSection>
							)}
						</>
					}
					columnDisplay={preferences.contentDisplay}
					variant="full-page"
					pagination={<Pagination {...paginationProps} />}
					selectionType="single"
					preferences={
						<CollectionPreferences
							{...COLLECTION_PREFERENCES}
							preferences={preferences}
							onConfirm={({ detail }) => setPreferences(detail)}
						/>
					}
					selectedItems={selectedWorkflows}
					onSelectionChange={({ detail }) => {
						setSelectedWorkflows(detail.selectedItems);
					}}
					onRowClick={({ detail }) => {
						setSelectedWorkflows([detail.item]);
					}}
					filter={
						<TextFilter
							{...filterProps}
							filteringPlaceholder="Search workflow"
							countText={filterCounter(filteredItemsCount)}
						/>
					}
				/>
			</SpaceBetween>
		</Container>
	);
}
